import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Table,
  Tabs,
  Tag,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text } = Typography;

const ApiIntegracoes = () => {
  const { message } = AntApp.useApp();
  const [integrationForm] = Form.useForm();
  const [providerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [integrationModal, setIntegrationModal] = useState(false);
  const [providerModal, setProviderModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [orgIntegrations, providersData] = await Promise.all([
        admin.getOrgIntegrations(),
        admin.getIntegrationProviders()
      ]);
      setIntegrations(Array.isArray(orgIntegrations) ? orgIntegrations : []);
      setProviders(Array.isArray(providersData) ? providersData : []);
    } catch {
      message.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openCreateIntegration = () => {
    setEditingIntegration(null);
    integrationForm.resetFields();
    integrationForm.setFieldsValue({ isActive: true, scope: 'default' });
    setIntegrationModal(true);
  };

  const openEditIntegration = (record) => {
    setEditingIntegration(record);
    integrationForm.setFieldsValue({
      organizationId: record.organizationId,
      providerId: record.providerId,
      name: record.name,
      description: record.description,
      clientId: record.clientId,
      clientSecret: record.clientSecret,
      endpointOverride: record.endpointOverride,
      costPerRequest: Number(record.costPerRequest || 0),
      maxRequestsPerDay: record.maxRequestsPerDay,
      scope: record.scope,
      isActive: record.isActive
    });
    setIntegrationModal(true);
  };

  const handleDeleteIntegration = async (id) => {
    try {
      await admin.deleteOrgIntegration(id);
      message.success('Excluída');
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao excluir');
    }
  };

  const handleIntegrationSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        ...values,
        providerId: Number(values.providerId),
        costPerRequest: values.costPerRequest ? Number(values.costPerRequest) : 0,
        maxRequestsPerDay: values.maxRequestsPerDay ? Number(values.maxRequestsPerDay) : null
      };
      if (editingIntegration) {
        await admin.updateOrgIntegration(editingIntegration.id, payload);
        message.success('Integração atualizada');
      } else {
        await admin.createOrgIntegration(payload);
        message.success('Integração criada');
      }
      setIntegrationModal(false);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const openCreateProvider = () => {
    setEditingProvider(null);
    providerForm.resetFields();
    setProviderModal(true);
  };

  const openEditProvider = (record) => {
    setEditingProvider(record);
    providerForm.setFieldsValue(record);
    setProviderModal(true);
  };

  const handleDeleteProvider = async (id) => {
    try {
      await admin.deleteIntegrationProvider(id);
      message.success('Provedor excluído');
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao excluir');
    }
  };

  const handleProviderSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingProvider) {
        await admin.updateIntegrationProvider(editingProvider.id, values);
        message.success('Provedor atualizado');
      } else {
        await admin.createIntegrationProvider(values);
        message.success('Provedor criado');
      }
      setProviderModal(false);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const integrationColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    {
      title: 'Provedor',
      dataIndex: ['provider', 'name'],
      key: 'provider',
      render: (value) => <Tag color="blue">{value || '-'}</Tag>
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (value) => value ? <Text code>{value.substring(0, 20)}...</Text> : '-'
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (value) => <Tag>{value}</Tag>
    },
    {
      title: 'Custo/Req',
      dataIndex: 'costPerRequest',
      key: 'costPerRequest',
      render: (value) => value ? `R$ ${Number(value).toFixed(4)}` : '-'
    },
    { title: 'Req/Dia', dataIndex: 'maxRequestsPerDay', key: 'maxRequestsPerDay' },
    { title: 'Hoje', dataIndex: 'requestsToday', key: 'requestsToday' },
    {
      title: 'Ativo',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Sim' : 'Não'}</Tag>
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditIntegration(record)} />
          </Tooltip>
          <Popconfirm
            title="Excluir integração?"
            onConfirm={() => handleDeleteIntegration(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Tooltip title="Excluir">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const providerColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (value) => <Tag color="geekblue">{value}</Tag>
    },
    { title: 'Auth Type', dataIndex: 'authType', key: 'authType' },
    {
      title: 'Base URL',
      dataIndex: 'baseUrl',
      key: 'baseUrl',
      render: (value) => value ? <Text code>{value}</Text> : '-'
    },
    {
      title: 'Ativo',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Sim' : 'Não'}</Tag>
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditProvider(record)} />
          </Tooltip>
          <Popconfirm
            title="Excluir provedor?"
            onConfirm={() => handleDeleteProvider(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Tooltip title="Excluir">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const providerModalContent = (
    <Modal
      title={editingProvider ? 'Editar Provedor' : 'Novo Provedor'}
      open={providerModal}
      onCancel={() => setProviderModal(false)}
      footer={null}
      destroyOnClose
      style={{ top: 0 }}
      maskClosable={false}
      closable={false}
    >
      <Form form={providerForm} layout="vertical" onFinish={handleProviderSubmit} style={{ marginTop: 16 }}>
        <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Descrição">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="baseUrl" label="Base URL">
          <Input placeholder="https://api.provedor.com" />
        </Form.Item>
        <Form.Item name="authType" label="Tipo de Autenticação">
          <Select>
            <Select.Option value="oauth2">OAuth2</Select.Option>
            <Select.Option value="api_key">API Key</Select.Option>
            <Select.Option value="basic">Basic Auth</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="authEndpoint" label="Auth Endpoint">
          <Input />
        </Form.Item>
        <Form.Item name="tokenEndpoint" label="Token Endpoint">
          <Input />
        </Form.Item>
        <Form.Item name="documentationUrl" label="URL de Documentação">
          <Input />
        </Form.Item>
        <Form.Item name="costPerRequest" label="Custo por Requisição">
          <InputNumber style={{ width: '100%' }} min={0} step={0.0001} />
        </Form.Item>
        <Form.Item name="isActive" label="Ativo" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>Salvar</Button>
            <Button onClick={() => setProviderModal(false)}>Cancelar</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Integrações de API</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateIntegration}>Nova Integração</Button>
        </Space>
      </div>

      <Tabs
        items={[
          {
            key: 'integrations',
            label: 'Integrações',
            children: (
              <Card>
                <Table
                  columns={integrationColumns}
                  dataSource={integrations}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showTotal: (total) => `Total: ${total}` }}
                />
              </Card>
            )
          },
          {
            key: 'providers',
            label: 'Provedores',
            children: (
              <Card
                extra={
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateProvider}>
                    Novo Provedor
                  </Button>
                }
              >
                <Table
                  columns={providerColumns}
                  dataSource={providers}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 10, showTotal: (total) => `Total: ${total}` }}
                />
              </Card>
            )
          }
        ]}
      />

      <Modal
        title={editingIntegration ? 'Editar Integração' : 'Nova Integração'}
        open={integrationModal}
        onCancel={() => setIntegrationModal(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={integrationForm} layout="vertical" onFinish={handleIntegrationSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="organizationId" label="ID da Organização" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="providerId" label="Provedor" rules={[{ required: true }]}>
            <Select placeholder="Selecione o provedor">
              {providers.map((provider) => (
                <Select.Option key={provider.id} value={provider.id}>{provider.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descrição">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="clientId" label="Client ID">
            <Input />
          </Form.Item>
          <Form.Item name="clientSecret" label="Client Secret">
            <Input.Password />
          </Form.Item>
          <Form.Item name="endpointOverride" label="Endpoint (override)">
            <Input placeholder="https://api.custom.com" />
          </Form.Item>
          <Form.Item name="scope" label="Scope">
            <Input placeholder="default" />
          </Form.Item>
          <Form.Item name="costPerRequest" label="Custo por Requisição">
            <InputNumber style={{ width: '100%' }} min={0} step={0.0001} />
          </Form.Item>
          <Form.Item name="maxRequestsPerDay" label="Máx. Requisições/Dia">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="isActive" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>Salvar</Button>
              <Button onClick={() => setIntegrationModal(false)}>Cancelar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {providerModalContent}
    </div>
  );
};

export default ApiIntegracoes;
